import {
	FilesOnGFA,
	GFADocumentType,
	GFAFileSection,
	GFAFullDetails,
} from "@dune/types";
import {
	Button,
	H3,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@dune/ui";
import { GFA_DOCUMENT_TYPES, gfaDocumentTypeLabels } from "@dune/vars";
import { Trash2 } from "lucide-react";
import {
	useLinkFileToGFA,
	useUnlinkFileFromGFA,
} from "../../api/mutations/gfa.mutations";
import { FileStatusChip } from "../common/files/FileStatusChip";
import { PreviewFileLink } from "../common/files/PreviewFileLink";
import { TableCellWithTooltip } from "../shared/TableCellWithTooltip";
import { AddGFADocumentButton } from "./AddGFADocumentButton";

interface GFADocumentsTableProps {
	gfaConstructionSite: GFAFullDetails;
	section: GFAFileSection;
	title?: string;
	isForm?: boolean;
}

export const GFADocumentsTable = ({
	gfaConstructionSite,
	section,
	title = "PIÈCES JUSTIFICATIVES",
	isForm = true,
}: GFADocumentsTableProps) => {
	const documentTypes = GFA_DOCUMENT_TYPES[section];

	const linkFileMutation = useLinkFileToGFA(gfaConstructionSite.id);
	const unlinkFileMutation = useUnlinkFileFromGFA(gfaConstructionSite.id);

	return (
		<div>
			<H3>{title}</H3>
			<div className="bg-white rounded-lg border">
				<Table className="table-fixed w-full">
					<TableHeader>
						<TableRow>
							<TableHead className="p-4">Type</TableHead>
							<TableHead className="p-4 text-center">
								Pièces justificatives
							</TableHead>
							<TableHead className="p-4 text-end">
								{!isForm && "Status"}
							</TableHead>
						</TableRow>
					</TableHeader>
					<TableBody>
						{documentTypes.map((documentType) => {
							const files = findFilesForDocumentType(
								documentType,
								gfaConstructionSite.filesOnGFA,
							);
							return (
								<TableRow key={documentType}>
									<TableCellWithTooltip
										tooltipContent={gfaDocumentTypeLabels[documentType]}
										className="p-4"
									>
										{gfaDocumentTypeLabels[documentType]}
									</TableCellWithTooltip>
									<TableCell className="truncate align-top p-4">
										<div className="flex flex-col gap-2 items-center">
											{files.map((file) => (
												<div key={file.file.id}>
													<PreviewFileLink file={file.file} />
												</div>
											))}
										</div>
									</TableCell>
									<TableCell className="truncate align-top p-4">
										<div className="flex flex-col gap-2 items-end">
											{files.map((file) => (
												<div
													key={file.file.id}
													className="flex items-center gap-2"
												>
													<FileStatusChip
														humanProcessingStatus={
															file.file.humanProcessingStatus
														}
													/>
													{isForm && (
														<Button
															variant="ghost"
															size="sm"
															type="button"
															onClick={() => {
																unlinkFileMutation.mutate(file.file.id);
															}}
															disabled={unlinkFileMutation.isPending}
														>
															<Trash2 className="h-4 w-4" />
														</Button>
													)}
												</div>
											))}
											{isForm && files.length === 0 && (
												<div>
													<AddGFADocumentButton
														gfaId={gfaConstructionSite.id}
														onUploadSuccess={(fileId: string) => {
															linkFileMutation.mutate({
																fileId,
																type: documentType,
															});
														}}
													/>
												</div>
											)}
										</div>
									</TableCell>
								</TableRow>
							);
						})}
					</TableBody>
				</Table>
			</div>
		</div>
	);
};

const findFilesForDocumentType = (
	documentType: GFADocumentType,
	files: FilesOnGFA[],
) => {
	return files.filter((file) => file.type === documentType);
};
